import { useAccessedByUserQuery } from "@sso/apis/userProducts";
import { DialogTitle } from "@sso/constant";
import { useAppSelector } from "@sso/hooks/useRedux";
import { selectDialog } from "@sso/store/slices/dialog";
import { LicencedSoftware } from "@sso/types";
import { CircleLoading } from "@sso/ui/CircleLoading";
import { Flex } from "@sso/ui/FlexBox";
import { Table, TD, TH } from "@sso/ui/Table";
import { Typography } from "@sso/ui/Typography";

import { DialogBody, DialogHeader } from "../components";
import { UserProductToggleRole } from "./UserProductToggleRole";

export const UserProductDialog = () => {
  const { id: userId } = useAppSelector(selectDialog);

  const { data: licencedSoftware, isLoading, isError } = useAccessedByUserQuery(userId as string, { skip: !userId });

  const data: LicencedSoftware[] = !licencedSoftware
    ? []
    : licencedSoftware.map((software: LicencedSoftware) => ({
        ...software,
        lastLogin: software.lastLogin ? new Date(software.lastLogin).toLocaleDateString() : "-",
        expiry: software.expiry ? new Date(software.expiry).toLocaleDateString() : "-",
      }));

  const DialogContent = () => {
    if (isLoading) {
      return <CircleLoading top="50px" blurBackground />;
    } else if (isError) {
      return <Typography margin="3rem 0 0">Something went wrong!</Typography>;
    } else if (data.length === 0) {
      return <Typography margin="3rem 0 0">There are no products for this user.</Typography>;
    }
    return (
      <Table>
        <thead>
          <tr>
            <TH textAlign="center">Product</TH>
            <TH textAlign="center">Licence</TH>
            <TH textAlign="center">Last login</TH>
            <TH textAlign="center">Roles</TH>
          </tr>
        </thead>
        <tbody>
          {data.map(product => (
            <tr key={product.softwareId}>
              <TD textAlign="center" padding="1rem 0.4rem">
                <Typography display="block" size="1.2rem">
                  {product.name}
                </Typography>
              </TD>
              <TD textAlign="center" padding="1rem 0.4rem">
                <Typography display="block" size="1.2rem">
                  {product.softwareLicence.name}
                </Typography>
                <Typography display="block" size="0.8rem">
                  Expires: {product.expiry}
                </Typography>
              </TD>
              <TD textAlign="center" padding="1rem 0.4rem">
                <Typography display="block" size="1.2rem">
                  {product.lastLogin}
                </Typography>
              </TD>
              <TD>
                {product.roles.map(role => (
                  <div
                    key={role.roleId}
                    style={{ display: "flex", alignContent: "center", flexDirection: "row", margin: "0.5rem" }}
                  >
                    <UserProductToggleRole
                      userId={userId as string}
                      softwareId={role.softwareId as string}
                      roleId={role.roleId as string}
                      hasRole={product.assignedRoles.includes(role.roleId)}
                    />
                    <Typography display="initial" size="0.8rem" style={{ paddingLeft: "0.5rem" }}>
                      {role.name}
                    </Typography>
                  </div>
                ))}
              </TD>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  return (
    <>
      <DialogHeader title={DialogTitle.USER_PRODUCT} />
      <DialogBody padding="0 0 1rem" minHeight="150px">
        <Flex position="relative" height="100%">
          <DialogContent />
        </Flex>
      </DialogBody>
    </>
  );
};
